import { Link } from "@tanstack/react-router";
import { Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import { Container } from "./Container";
import logoAsset from "@/assets/durafoam-logo.webp.asset.json";
import { LOCATION_GROUPS, LOCATION_INDEX } from "@/lib/location-index";

const services = [
  { label: "Foam Roofing", href: "/foam-roofing" },
  { label: "Foam Roof Recoating", href: "/foam-roof-coating-recoating" },
  { label: "Roof Leak Repair", href: "/roof-leak-repair" },
  { label: "Storm & Hail Damage Repair", href: "/storm-damage-repair" },
  { label: "Hail Damage Roof Inspection", href: "/hail-damage-roof-inspection-phoenix" },
  { label: "Tile Roofing", href: "/tile-roofing" },
  { label: "Shingle Roofing", href: "/shingle-roofing" },
  { label: "Commercial Roofing", href: "/commercial-roofing" },
  { label: "Commercial Foam Roofing", href: "/commercial-foam-roofing" },
  { label: "Commercial Roof Repair", href: "/commercial-roof-repair" },
  { label: "Commercial Roof Coating", href: "/commercial-roof-coating" },
  { label: "Warehouse Roofing", href: "/warehouse-roofing" },
  { label: "Roofing Costs", href: "/roofing-costs-phoenix" },
  { label: "Free Roof Inspection", href: "/free-roof-inspection-phoenix" },
];


const headingCls = "text-[11px] font-semibold uppercase tracking-[0.12em] text-white mb-4";
const linkCls = "text-white/60 hover:text-white text-sm transition-colors";

export function SiteFooter() {
  return (
    <footer className="bg-[var(--primary-deep)] text-white/70">
      <Container className="py-14 lg:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5">
              <img
                src={logoAsset.url}
                alt="Durafoam Roofing"
                height={110}
                className="h-[110px] w-auto"
              />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/65">
              Family-owned foam roofing specialists serving the Phoenix Valley with honest assessments
              since 1989.
            </p>
            <div className="mt-5 flex items-center gap-2">
              {[
                { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/durafoamroofing" },
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/durafoamroofing" },
                { Icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@DurafoamRoofing" },
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/durafoam-roofing/" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Durafoam Roofing on ${label}`}
                  className="grid h-10 w-10 place-items-center rounded-md text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h2 className={headingCls}>Services</h2>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.href}>
                  <Link to={s.href} className={linkCls}>
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service areas — all groups */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className={headingCls + " mb-0"}>Service Areas</h2>
              <Link to="/service-areas" className="text-xs font-semibold text-white/90 hover:text-white underline underline-offset-2">
                View all →
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-x-5 gap-y-4 md:grid-cols-3 lg:grid-cols-5">
              {LOCATION_GROUPS.map((group) => (
                <div key={group.id}>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-white/75">
                    {group.label}
                  </p>
                  <ul className="space-y-1.5">
                    {group.slugs.map((slug) => {
                      const loc = LOCATION_INDEX[slug];
                      if (!loc) return null;
                      return (
                        <li key={slug}>
                          <Link to={loc.path} className={linkCls}>
                            {loc.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* NAP */}
          <div>
            <h2 className={headingCls}>Contact</h2>
            <address className="not-italic text-sm space-y-1.5 text-white/70">
              <p className="text-white font-medium">Durafoam Roofing LLC</p>
              <p>2810 E Mohawk Ln</p>
              <p>Phoenix, AZ 85050</p>
              <p className="pt-2">
                <a href="tel:+14809415373" className="hover:text-white">
                  Call: 480-941-5373
                </a>
              </p>
              <p>
                <a href="mailto:quality@durafoaminc.com" className="hover:text-white">
                  quality@durafoaminc.com
                </a>
              </p>
            </address>
            <div className="mt-4 space-y-1 text-xs text-white/55">
              <p>Licensed ROC223900 L-42</p>
              <p>BBB Accredited Since 2008</p>
              <p>Family Owned Since 1989</p>
              <p className="pt-1">
                <Link to="/rosie-on-the-house" className="hover:text-white underline underline-offset-2">
                  Rosie on the House Partner Since 1996
                </Link>
              </p>
            </div>
          </div>
        </div>
      </Container>

      <div className="border-t border-white/10">
        <Container className="flex flex-col gap-3 py-6 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Durafoam Roofing LLC. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </Container>
      </div>
    </footer>
  );
}
